import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { ChevronLeft, Clock, FileEdit, Mail, PhoneCall } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { getSlaStatus } from '@/lib/utils/slaStatus'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import type { ParentTicket, TicketUpdate } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { mapApiAttachments } from '@/lib/utils/parentTicketAttachments'
import { PageHeader } from '@/components/common/PageHeader'
import { apiClient } from '@/lib/api/client'

import CommentThread from '@/components/helpdesk/TicketDetail/CommentThread'
import TicketUpdateModal from '@/components/helpdesk/TicketDetail/TicketUpdateModal'
import CommentComposer from '@/components/helpdesk/TicketDetail/CommentComposer'
import OverviewTabPanel from '@/components/helpdesk/TicketDetail/overview/OverviewTabPanel'
import TaskListPanel from '@/components/helpdesk/TicketDetail/tasks/TaskListPanel'
import { isTaskBreached } from '@/components/helpdesk/TicketDetail/tasks/taskUtils'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { childTicketService } from '@/lib/services/childTicketService'
import { useSupportTypeOptions } from '@/hooks/useSupportTypeOptions'
import {
  defaultCreatePayloadForSourceField,
  type TaskSourceField,
} from '@/components/helpdesk/TicketDetail/tasks/taskSourceFieldUtils'
import { geographicService, type GeographicOption } from '@/lib/services/geographicService'
import {
  assignmentMethodBadgeColor,
  resolveAssignmentMethodLabel,
} from '@/lib/utils/assignmentMethod'
import { AliceCallOutConfirmModal, ComposeEmailDrawer } from '@/components/helpdesk'
import { ALICE_CALL_OUT_ENABLED } from '@/lib/vicidial'
import { openVicidialCall } from '@/lib/vicidialCallIntent'
import {
  buildVoiceAiCallPayload,
  type VoiceAiCallPayload,
} from '@/lib/voiceAi/buildVoiceAiCallPayload'

type Option = { label: string; value: string }

type LocationDetails = {
  region?: string | null
  areaCluster?: string | null
  province?: string | null
  town?: string | null
}

function geographicLabel(options: GeographicOption[], id?: number | null): string | null {
  if (id == null) return null
  return options.find((option) => option.id === id)?.label ?? null
}

function readGeographicIds(meta: ParentTicketWithMeta | undefined) {
  if (!meta) {
    return {
      regionId: undefined as number | undefined,
      areaClusterId: undefined as number | undefined,
      provinceId: undefined as number | undefined,
      townId: undefined as number | undefined,
    }
  }

  const ext = meta as Record<string, unknown>
  return {
    regionId:
      meta.region_id ??
      (typeof ext.regionId === 'number' ? ext.regionId : undefined),
    areaClusterId:
      meta.area_cluster_id ??
      (typeof ext.areaClusterId === 'number' ? ext.areaClusterId : undefined),
    provinceId:
      meta.province_id ??
      (typeof ext.provinceId === 'number' ? ext.provinceId : undefined),
    townId:
      meta.town_id ??
      (typeof ext.townId === 'number' ? ext.townId : undefined),
  }
}

const TAB_OVERVIEW = 0
const TAB_TASKS = 1
const TAB_ACTIVITY = 2

function severityColorScheme(severity: ParentTicket['severity']): string {
  const n = Number(severity)
  if (n >= 4) return 'red'
  if (n >= 3) return 'orange'
  if (n >= 2) return 'blue'
  return 'gray'
}

export default function ParentTicketViewPage() {
  const colors = useThemeColors()
  const { labelByCode: supportTypeLabels } = useSupportTypeOptions()
  const { labelMap: severityLabels } = useTicketOptions('severity')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const ticket = useParentTicketStore((state) => {
    if (!id) return undefined
    return state.tickets.find((t) => {
      const row = t as ParentTicketWithMeta & { ticket_number?: string; ticketNumber?: string }
      return (
        String(t.id) === String(id) ||
        String(row.ticket_number ?? '') === String(id) ||
        String(row.ticketNumber ?? '') === String(id)
      )
    })
  })
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const meta = ticket as ParentTicketWithMeta | undefined
  /** Numeric PK when loaded; fall back to route param (may be ticket_number). */
  const apiTicketId = ticket?.id ? String(ticket.id) : id
  const [fetchError, setFetchError] = useState<string | null>(null)
  /** Full-page loader until first ticket payload exists (avoids "not found" flash on direct reload). */
  const showFullPageLoader = !ticket && fetchError == null && Boolean(id)
  const [statusOptions, setStatusOptions] = useState<Option[]>([])
  const [ticketUpdates, setTicketUpdates] = useState<TicketUpdate[]>([])
  const [editingUpdate, setEditingUpdate] = useState<TicketUpdate | null>(null)
  const [feScheduleRefreshing, setFeScheduleRefreshing] = useState(false)
  const [tabIndex, setTabIndex] = useState(TAB_OVERVIEW)
  const [pendingOpenTaskId, setPendingOpenTaskId] = useState<number | null>(null)
  const [pendingCreateDefaults, setPendingCreateDefaults] = useState<Partial<ChildTicketTask> | null>(null)
  const [tasks, setTasks] = useState<ChildTicketTask[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null)
  const { isOpen: isCallOutOpen, onOpen: onCallOutOpen, onClose: onCallOutClose } = useDisclosure()
  const {
    isOpen: isComposeEmailOpen,
    onOpen: onComposeEmailOpen,
    onClose: onComposeEmailClose,
  } = useDisclosure()
  const [callOutPayload, setCallOutPayload] = useState<VoiceAiCallPayload | null>(null)

  useEffect(() => {
    const taskParam = searchParams.get('task')
    if (!taskParam) return
    const taskId = Number(taskParam)
    if (!Number.isFinite(taskId) || taskId <= 0) return
    setTabIndex(TAB_TASKS)
    setPendingOpenTaskId(taskId)
    const next = new URLSearchParams(searchParams)
    next.delete('task')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const geographicIds = useMemo(() => readGeographicIds(meta), [meta])

  const fetchTicketUpdates = useCallback(async () => {
    if (!apiTicketId) return
    const res = await TicketService.getTicketUpdates(apiTicketId, 1, 100)
    if (res.success && res.data) {
      const raw = res.data.data ?? res.data
      setTicketUpdates(Array.isArray(raw) ? raw : [])
    }
  }, [apiTicketId])

  const ticketNumberDisplay = (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number
  const subjectDisplay = (meta as ParentTicketWithMeta & { subject?: string })?.subject

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  useEffect(() => {
    setFetchError(null)
  }, [id])

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<{ data: Array<{ id: number; code: string; label?: string | null }> }>(
        '/service-catalog/parent-ticket-statuses',
        { params: { per_page: 100 } }
      )
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          setStatusOptions(
            items.map((s) => ({
              label: s.label || s.code,
              value: s.code,
            }))
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    fetchTicketUpdates()
  }, [fetchTicketUpdates])

  useEffect(() => {
    const { regionId, areaClusterId, provinceId, townId } = geographicIds

    if (!regionId && !areaClusterId && !provinceId && !townId) {
      setLocationDetails(null)
      return
    }

    let cancelled = false

    async function resolveLocationLabels() {
      try {
        const [regions, areaClusters] = await Promise.all([
          geographicService.getRegions(),
          geographicService.getAreaClusters(),
        ])

        const provinces = areaClusterId
          ? await geographicService.getProvincesByCluster(areaClusterId)
          : provinceId
            ? await geographicService.getProvinces()
            : []
        const towns = provinceId
          ? await geographicService.getTownsByProvince(provinceId)
          : townId
            ? await geographicService.getTowns()
            : []

        if (cancelled) return

        setLocationDetails({
          region: geographicLabel(regions, regionId),
          areaCluster: geographicLabel(areaClusters, areaClusterId),
          province: geographicLabel(provinces, provinceId),
          town: geographicLabel(towns, townId),
        })
      } catch {
        if (!cancelled) setLocationDetails(null)
      }
    }

    void resolveLocationLabels()

    return () => {
      cancelled = true
    }
  }, [
    geographicIds.regionId,
    geographicIds.areaClusterId,
    geographicIds.provinceId,
    geographicIds.townId,
  ])

  const fetchTasks = useCallback(async () => {
    if (!apiTicketId) return
    setTasksLoading(true)
    try {
      const list = await childTicketService.list(apiTicketId)
      setTasks(list)
    } catch {
      // Overview widget is optional; task tab shows errors via TaskListPanel
    } finally {
      setTasksLoading(false)
    }
  }, [apiTicketId])

  const refreshParentTicket = useCallback(async () => {
    if (!apiTicketId) return
    const res = await TicketService.getParentTicket(apiTicketId)
    if (res.success && res.data) {
      mergeTicket(res.data)
    }
  }, [apiTicketId, mergeTicket])

  const handleOpenProcurementTask = useCallback((taskId: number) => {
    setTabIndex(TAB_TASKS)
    setPendingOpenTaskId(taskId)
  }, [])

  const handleCreateFieldTask = useCallback((sourceField: TaskSourceField) => {
    setTabIndex(TAB_TASKS)
    setPendingCreateDefaults(defaultCreatePayloadForSourceField(sourceField))
  }, [])

  const handleOpenFieldTask = useCallback((task: ChildTicketTask) => {
    setTabIndex(TAB_TASKS)
    setPendingOpenTaskId(task.id)
  }, [])

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<{ data: Array<{ code: string; scope?: string | null; is_terminal?: boolean }> }>(
        '/service-catalog/child-ticket-statuses',
        { params: { per_page: 100 } },
      )
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          window.localStorage.setItem(
            'helpdesk.child_ticket_status_catalog',
            JSON.stringify(
              items.map((item) => ({
                code: item.code,
                scope: item.scope ?? null,
                is_terminal: item.is_terminal === true,
              })),
            ),
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    void fetchTasks()
  }, [fetchTasks])

  useEffect(() => {
    if (!id) return

    const ac = new AbortController()
    setFetchError(null)

    TicketService.getParentTicket(id, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        if (res.success && res.data) {
          mergeTicket(res.data)
          setFetchError(null)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || axios.isCancel(err)) return
        setFetchError('Failed to load ticket')
      })

    return () => {
      ac.abort()
    }
  }, [id, mergeTicket])

  if (!id) {
    return null
  }

  if (showFullPageLoader) {
    return (
      <Box
        bg={colors.bg}
        p={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="240px"
      >
        <Spinner size="lg" thickness="3px" color={colors.primary} />
        <Text ml={3} color={colors.textSecondary} fontWeight="500">
          Loading ticket…
        </Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>{fetchError}</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>Ticket not found.</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  const storeData = meta?.store
  const incidentLocation = meta?.incidentLocation
  const requestData = meta?.request

  const handleOpenCallOut = () => {
    if (!requestData) return
    const row: Record<string, unknown> = {
      id: requestData.id,
      request_number: requestData.serialNumber ?? requestData.id,
      contact_name: requestData.contactName,
      contact_number: requestData.contactNumber,
      contact_email: requestData.contactEmail,
      description: requestData.description ?? requestData.issueDescription,
      service_type: requestData.serviceType,
      client_code: requestData.clientCode,
      extracted_fields: {},
    }
    const payload = buildVoiceAiCallPayload(row)
    setCallOutPayload(payload)
    onCallOutOpen()
  }

  const requestDbId = requestData?.requestDbId
  const hasLinkedRequest =
    typeof requestDbId === 'number' && Number.isSafeInteger(requestDbId) && requestDbId > 0
  const canCallRequestor = Boolean(hasLinkedRequest && requestData?.contactNumber?.trim())
  const canEmailRequestor = Boolean(
    hasLinkedRequest &&
      requestData?.contactEmail?.trim()
  )
  const composeEmailRow: Record<string, unknown> | null =
    requestData && hasLinkedRequest
      ? {
          id: requestDbId,
          requestDbId,
          request_number: requestData.id,
          contact_name: requestData.contactName,
          contact_number: requestData.contactNumber,
          contact_email: requestData.contactEmail,
          description: requestData.description ?? requestData.issueDescription,
        }
      : null

  const handleOpenPhone = () => {
    if (!hasLinkedRequest || !requestDbId || !requestData?.contactNumber?.trim()) return
    openVicidialCall({
      requestId: requestDbId,
      requestLabel: `#${ticketNumberDisplay ?? id} · ${requestData.serialNumber ?? requestData.id}`,
      contactName: requestData.contactName?.trim() || 'Requestor',
      contactNumber: requestData.contactNumber,
      placement: 'right',
    })
  }
  const ticketWithStoreCode = ticket as typeof ticket & { store_code?: string }
  const storeCode = meta?.store_code || ticketWithStoreCode.store_code || storeData?.storeCode
  const overviewStoreDetails = storeData
    ? {
        locationType: 'store' as const,
        businessUnitName: storeData.businessUnitName ?? null,
        storeName: storeData.storeName ?? null,
        branchCode: storeCode ?? storeData.storeCode ?? storeData.branchCode ?? null,
        contactPerson: storeData.contactPerson ?? null,
        email: storeData.storeEmail ?? null,
        contactNumber: storeData.storeContact ?? storeData.contactNumber ?? null,
        storeAddress: storeData.branchAddress ?? storeData.locationAddress ?? null,
        latitude: storeData.latitude ?? storeData.location?.latitude ?? null,
        longitude: storeData.longitude ?? storeData.location?.longitude ?? null,
        locationSource:
          (storeData as { locationSource?: 'geocoded' | 'fallback' }).locationSource ?? null,
      }
    : incidentLocation
      ? {
          locationType: incidentLocation.type ?? 'location',
          businessUnitName: null,
          storeName: incidentLocation.name ?? null,
          branchCode: null,
          contactPerson: null,
          email: null,
          contactNumber: null,
          storeAddress: incidentLocation.address ?? incidentLocation.name ?? null,
          latitude: incidentLocation.latitude ?? null,
          longitude: incidentLocation.longitude ?? null,
          locationSource: null,
          municipality: incidentLocation.municipality ?? null,
          province: incidentLocation.province ?? null,
          country: incidentLocation.country ?? null,
        }
      : null

  const ticketPropsForProperties = {
    ticketNumber: (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number ?? undefined,
    description: ticket.description,
    notes: (meta as ParentTicketWithMeta & { notes?: string })?.notes ?? undefined,
    attachments: mapApiAttachments((meta as Record<string, unknown>)?.attachments),
    scheduledAt:
      (meta as ParentTicketWithMeta & { scheduledAt?: string })?.scheduledAt ??
      (ticket as ParentTicket & { scheduledAt?: string })?.scheduledAt ??
      undefined,
    requestedAt: ticket.requestedAt,
    ticketSubmittedAt: ticket.ticketSubmittedAt,
    ageingLabel: ticket.ageingLabel,
    holdTimeLabel: ticket.holdTimeLabel,
    isAgePaused: ticket.isAgePaused,
    assets: ((meta as Record<string, unknown>)?.assets ?? undefined) as
      | { id: number; tag_number?: string | null; model?: string | null }[]
      | undefined,
    source: (meta as ParentTicketWithMeta & { source?: string })?.source ?? undefined,
    severity: ticket.severity,
    priority: ticket.severity,
    supportType: (meta as ParentTicketWithMeta & { support_type?: string })?.support_type ?? undefined,
    subject: (meta as ParentTicketWithMeta & { subject?: string })?.subject ?? undefined,
    status: ticket.status,
    agentName:
      ticket.agentName ??
      (meta?.agent
        ? [meta.agent.first_name, meta.agent.last_name].filter(Boolean).join(' ').trim() ||
          meta.agent.user_login
        : undefined),
    clientReferenceNumber: ticket.clientReferenceNumber ?? undefined,
    purchaseOrder: meta?.purchaseOrder ?? undefined,
    hasPartsNeeded:
      (meta as ParentTicketWithMeta & { has_parts_needed?: boolean; hasPartsNeeded?: boolean })?.has_parts_needed ??
      (meta as ParentTicketWithMeta & { hasPartsNeeded?: boolean })?.hasPartsNeeded ??
      undefined,
    ticketParts: Array.isArray((meta as Record<string, unknown>)?.ticket_parts)
      ? ((meta as Record<string, unknown>).ticket_parts as import('@/types/parentTicket').TicketPart[])
      : Array.isArray((meta as Record<string, unknown>)?.ticketParts)
        ? ((meta as Record<string, unknown>).ticketParts as import('@/types/parentTicket').TicketPart[])
        : undefined,
  }

  const sharedTicketPropertiesProps = {
    request: requestData,
    ticket: ticketPropsForProperties,
    ticketId: apiTicketId,
    /** Require resolved store from client DB (not raw store_id alone) so FE recommender uses correct SIMA tenant */
    storeId: storeData?.storeId ?? null,
    storeCode: storeCode ?? undefined,
    clientCode:
      storeData?.clientCode ??
      requestData?.clientCode ??
      (meta as ParentTicketWithMeta & { clientCode?: string })?.clientCode ??
      undefined,
    primaryEngineer: meta?.primaryEngineer,
    backupEngineer: meta?.additionalEngineer ?? meta?.backupEngineer,
    backupEngineers: meta?.additionalEngineers ?? meta?.backupEngineers,
    storeDetails: overviewStoreDetails,
    locationDetails,
    onWorkPermitUpload: async (ticketId: string, _file: File) => {
      const res = await TicketService.getParentTicket(ticketId)
      if (res.success && res.data) mergeTicket(res.data)
      fetchTicketUpdates()
    },
    onSchedulesChange: async () => {
      await fetchTicketUpdates()
      setFeScheduleRefreshing(true)
      try {
        const res = await TicketService.getParentTicket(apiTicketId!)
        if (res.success && res.data) mergeTicket(res.data)
      } finally {
        setFeScheduleRefreshing(false)
      }
    },
    feScheduleRefreshing,
    onNotesUpdate: async () => {
      if (!apiTicketId) return
      const res = await TicketService.getParentTicket(apiTicketId)
      if (res.success && res.data) mergeTicket(res.data)
    },
    onPartsChange: async () => {
      await fetchTicketUpdates()
      await fetchTasks()
      if (!apiTicketId) return
      const res = await TicketService.getParentTicket(apiTicketId)
      if (res.success && res.data) mergeTicket(res.data)
    },
    onOpenProcurementTask: handleOpenProcurementTask,
    onCreateFieldTask: handleCreateFieldTask,
    onOpenFieldTask: handleOpenFieldTask,
    ticketTasks: tasks,
    onStoreUpdated: async () => {
      if (!apiTicketId) return
      const res = await TicketService.getParentTicket(apiTicketId)
      if (res.success && res.data) mergeTicket(res.data)
    },
    onAssetsUpdated: async () => {
      if (!apiTicketId) return
      const res = await TicketService.getParentTicket(apiTicketId)
      if (res.success && res.data) mergeTicket(res.data)
    },
    isReadOnly: false,
    embedded: true,
    feSchedules: ticket?.feSchedules ?? [],
  }

  const supportTypeRaw = ticketPropsForProperties.supportType
  const purchaseOrderDisplay =
    meta?.purchaseOrder?.po_number != null
      ? [meta.purchaseOrder.po_number, meta.purchaseOrder.description].filter(Boolean).join(' — ')
      : meta?.clientPurchaseOrderId
        ? `PO #${meta.clientPurchaseOrderId}`
        : undefined
  const assetsCount = ticketPropsForProperties.assets?.length ?? 0
  const storeIdForTasks =
    storeData?.storeId ?? (meta as ParentTicketWithMeta & { store_id?: number | null })?.store_id ?? null
  const activityCount = ticketUpdates.length
  const taskCount = tasks.length
  const statusLabel =
    statusOptions.find((o) => o.value === ticket.status)?.label ?? ticket.status
  const slaStatus = getSlaStatus(ticket.slaDueAt)
  const severityLabel =
    ticket.severity != null
      ? severityLabels[String(ticket.severity)] ?? String(ticket.severity)
      : null
  const assignmentMethod =
    ticket.assignmentMethod ??
    (meta as ParentTicketWithMeta & { assignment_method?: string })?.assignment_method ??
    null
  const assignmentMethodLabel = resolveAssignmentMethodLabel(assignmentMethod)
  const hasBreachedTask = tasks.some(isTaskBreached)
  const slaBadgeColorScheme =
    slaStatus.urgency === 'overdue' ? 'red' : slaStatus.urgency === 'due_soon' ? 'orange' : 'green'

  const handleStatusSaved = (status: ParentTicket['status']) => {
    mergeTicket({ ...ticket, status } as ParentTicket)
  }

  return (
    <Box
      bg={colors.bg}
      p={{ base: 3, md: 4 }}
      pb={8}
      w="100%"
      minW={0}
      minH="calc(100vh - 80px - 48px)"
      display="flex"
      flexDir="column"
    >
      <PageHeader
        mb={3}
        start={
          <Button
            variant="ghost"
            leftIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/tickets')}
            px={2}
            color={colors.text}
            _hover={{ bg: colors.sidebarHoverBg }}
          >
            Back to tickets
          </Button>
        }
      />

      <Card
        variant="outline"
        w="100%"
        bg={colors.cardBg}
        borderColor={colors.border}
        flex="1"
        minW={0}
        shadow="sm"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <CardBody p={0} display="flex" flexDirection="column" flex="1" minH={0}>
          {/* Hero */}
          <Box
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            bg={colors.sectionBg ?? colors.cardBg}
            borderBottom="1px solid"
            borderColor={colors.borderLight}
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap={4}
              align={{ base: 'flex-start', lg: 'flex-start' }}
              justify="space-between"
            >
              <Box minW={0} flex="1">
                <HStack spacing={2} flexWrap="wrap" align="center" mb={1.5}>
                  <Heading size="md" fontWeight="700" color={colors.heading} noOfLines={2} lineHeight="snug">
                    #{ticketNumberDisplay ?? id}
                  </Heading>
                  <Badge colorScheme="blue" fontSize="0.7rem" px={2} py={0.5} borderRadius="md" textTransform="none">
                    {statusLabel}
                  </Badge>
                  {ticket.reopenedAt ? (
                    <Badge colorScheme="orange" fontSize="0.65rem" px={2} py={0.5} borderRadius="md" textTransform="none">
                      Reopened
                    </Badge>
                  ) : null}
                  {slaStatus.urgency !== 'none' ? (
                    <Badge
                      colorScheme={slaBadgeColorScheme}
                      fontSize="0.65rem"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      <HStack as="span" spacing={1} display="inline-flex" alignItems="center">
                        <Clock size={11} aria-hidden />
                        <Text as="span">{slaStatus.toneLabel ?? slaStatus.label}</Text>
                      </HStack>
                    </Badge>
                  ) : null}
                  {severityLabel ? (
                    <Badge
                      colorScheme={severityColorScheme(ticket.severity)}
                      fontSize="0.65rem"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      {severityLabel}
                    </Badge>
                  ) : null}
                  {assignmentMethodLabel ? (
                    <Badge
                      colorScheme={assignmentMethodBadgeColor(assignmentMethod)}
                      fontSize="0.65rem"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                    >
                      {assignmentMethodLabel}
                    </Badge>
                  ) : null}
                </HStack>
                {subjectDisplay ? (
                  <Text fontSize="md" fontWeight="600" color={colors.text} mb={1} noOfLines={2}>
                    {subjectDisplay}
                  </Text>
                ) : null}
                <Text fontSize="sm" color={colors.textSecondary}>
                  {ticket.clientName ? ticket.clientName : null}
                  {ticket.clientName && storeCode ? ' · ' : null}
                  {storeCode ? `Store ${storeCode}` : null}
                  {!ticket.clientName && !storeCode ? '—' : null}
                </Text>
              </Box>
              <HStack spacing={2} flexWrap="wrap" justify={{ base: 'flex-start', lg: 'flex-end' }}>
                {ticket.clientOrg ? (
                  <Badge variant="subtle" colorScheme="gray" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                    {ticket.clientOrg}
                  </Badge>
                ) : null}
                {storeCode ? (
                  <Badge variant="subtle" colorScheme="purple" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                    Store {storeCode}
                  </Badge>
                ) : null}
                {ALICE_CALL_OUT_ENABLED ? (
                  <Button
                    size="sm"
                    leftIcon={<PhoneCall size={14} />}
                    colorScheme="purple"
                    variant="outline"
                    onClick={handleOpenCallOut}
                    title="Call via ALICE AI"
                  >
                    ALICE
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  leftIcon={<PhoneCall size={14} />}
                  colorScheme="green"
                  variant="solid"
                  isDisabled={!canCallRequestor}
                  onClick={handleOpenPhone}
                  title={canCallRequestor ? undefined : hasLinkedRequest ? 'Contact phone required to call out' : 'Linked request required to call out'}
                >
                  Call out
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Mail size={14} />}
                  colorScheme="blue"
                  variant="solid"
                  isDisabled={!canEmailRequestor}
                  onClick={onComposeEmailOpen}
                  title={
                    canEmailRequestor
                      ? undefined
                      : 'Linked ticket request with contact email required'
                  }
                >
                  Email requestor
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FileEdit size={14} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => navigate(`/tickets/${apiTicketId}/edit`)}
                >
                  Edit ticket
                </Button>
              </HStack>
            </Flex>
            {!hasLinkedRequest && requestData?.contactNumber?.trim() ? (
              <Text mt={2} fontSize="xs" color={colors.textSecondary}>
                Call out requires a linked ticket request.
              </Text>
            ) : null}
          </Box>

          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            flex="1"
            display="flex"
            flexDirection="column"
            minH={0}
            colorScheme="blue"
            variant="line"
            isLazy
          >
            <TabList px={{ base: 4, md: 5 }} borderBottomColor={colors.borderLight}>
              <Tab fontSize="sm" fontWeight="600">
                Overview
              </Tab>
              <Tab fontSize="sm" fontWeight="600">
                <HStack spacing={2}>
                  <Text>Tasks</Text>
                  {taskCount > 0 ? (
                    <Badge colorScheme="purple" borderRadius="full" fontSize="0.65rem" px={1.5}>
                      {taskCount}
                    </Badge>
                  ) : null}
                  {hasBreachedTask ? (
                    <Box w="6px" h="6px" borderRadius="full" bg="red.500" flexShrink={0} aria-hidden />
                  ) : null}
                </HStack>
              </Tab>
              <Tab fontSize="sm" fontWeight="600">
                <HStack spacing={2}>
                  <Text>Activity & Comments</Text>
                  {activityCount > 0 ? (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="0.65rem" px={1.5}>
                      {activityCount}
                    </Badge>
                  ) : null}
                </HStack>
              </Tab>
            </TabList>

            <TabPanels flex="1" minH={0} overflow="hidden">
              {/* Overview */}
              <TabPanel px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} overflowY="auto" h="100%">
                <OverviewTabPanel
                  ticket={ticket}
                  meta={meta}
                  ticketId={apiTicketId!}
                  tasks={tasks}
                  ticketUpdates={ticketUpdates}
                  statusOptions={statusOptions}
                  supportTypeLabels={supportTypeLabels}
                  supportTypeRaw={supportTypeRaw}
                  storeCode={storeCode}
                  purchaseOrderDisplay={purchaseOrderDisplay}
                  sharedTicketPropertiesProps={sharedTicketPropertiesProps}
                  assetsCount={assetsCount}
                  onTabChange={setTabIndex}
                  onStatusSaved={handleStatusSaved}
                  onRefreshActivity={fetchTicketUpdates}
                  onCsatSent={refreshParentTicket}
                  onFeVisitsCreated={async () => {
                    await Promise.all([fetchTasks(), refreshParentTicket()])
                  }}
                  tabTasks={TAB_TASKS}
                  tabActivity={TAB_ACTIVITY}
                />
              </TabPanel>

              {/* Tasks */}
              <TabPanel px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} overflowY="auto" h="100%">
                <TaskListPanel
                  parentTicketId={apiTicketId!}
                  tasks={tasks}
                  loading={tasksLoading}
                  onRefresh={fetchTasks}
                  onParentRefresh={refreshParentTicket}
                  ticketPropertiesProps={sharedTicketPropertiesProps}
                  storeId={storeIdForTasks}
                  assetsCount={assetsCount}
                  openTaskId={pendingOpenTaskId}
                  onOpenTaskHandled={() => setPendingOpenTaskId(null)}
                  pendingCreateDefaults={pendingCreateDefaults}
                  onPendingCreateHandled={() => setPendingCreateDefaults(null)}
                />
              </TabPanel>

              {/* Activity & Comments */}
              <TabPanel
                px={{ base: 4, md: 5 }}
                py={{ base: 3, md: 4 }}
                display="flex"
                flexDirection="column"
                h="100%"
                overflow="hidden"
              >
                <Box flex="1" minH={0} display="flex" flexDirection="column">
                  <CommentThread
                    ticketId={apiTicketId!}
                    updates={ticketUpdates}
                    onRefresh={fetchTicketUpdates}
                    onEdit={(u) => setEditingUpdate(u)}
                    embedded
                    flex="1"
                    minH={0}
                  />
                  <CommentComposer
                    ticketId={apiTicketId!}
                    onSubmitted={fetchTicketUpdates}
                    compact
                    placeholder="Write a comment…"
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      <TicketUpdateModal
        isOpen={editingUpdate !== null}
        onClose={() => setEditingUpdate(null)}
        ticketId={apiTicketId!}
        ticketNumber={(meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number}
        onSubmitted={fetchTicketUpdates}
        editingUpdate={editingUpdate}
      />

      {ALICE_CALL_OUT_ENABLED && callOutPayload ? (
        <AliceCallOutConfirmModal
          isOpen={isCallOutOpen}
          onClose={onCallOutClose}
          payload={callOutPayload}
        />
      ) : null}


      <ComposeEmailDrawer
        isOpen={isComposeEmailOpen}
        onClose={onComposeEmailClose}
        row={composeEmailRow}
        variant="thread"
        onSent={() => {
          void fetchTicketUpdates()
        }}
      />
    </Box>
  )
}
